import {
  Box,
  DarkTooltip,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  Add as AddIcon,
  Close,
  InsertDriveFile as InsertDriveFileIcon,
  KeyboardBackspace,
  KeyboardTab,
  Person as PersonIcon,
  QuestionAnswer,
} from '@mui/icons-material';
import { type ReactNode, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BrandLogoChat,
  ChatMenuStack,
  ChatPageBoxContainer,
  ChatPageSidebarContainer,
  HelpModal,
  MenuIcon,
  NewChatButton,
  ProfileModal,
  ReportUsageCard,
  SidebarIcon,
} from 'components/ChatBV/components';
import { ProfileSection } from 'components/ChatBV/components/Sidebar/ProfileSection';
import { FEEDBACK_FORM_URL } from 'components/ChatBV/constants';
import { DeepSearchContext } from 'components/DeepSearch/context';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

import { DeepSearchConversationList } from './DeepSearchConversationList';
import {
  AccountBox,
  AccountNavText,
  CloseSidebarButton,
  MobileNavItemButton,
  MobileProfileBox,
  ScrollableConversationBox,
  SidebarAvatar,
  SidebarNavText,
} from './DeepSearchSidebar.styles';

interface DeepSearchSidebarProps {
  onRecentReportsClick?: () => void;
  onAccountClick?: () => void;
  onUpgradePlanClick?: () => void;
  onLogout?: () => void;
}

const MobileNavItem = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) => (
  <MobileNavItemButton type="button" onClick={onClick}>
    {children}
  </MobileNavItemButton>
);

export const DeepSearchSidebar = ({
  onRecentReportsClick,
  onAccountClick,
  onUpgradePlanClick,
  onLogout,
}: DeepSearchSidebarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { isDesktop } = useScreenSize();
  const navigate = useNavigate();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const { startNewConversation } = useContext(DeepSearchContext);
  const [collapsed, setCollapsed] = useState(false);
  const { reportsRun, reportLimit, reportsRunPercentage, isLimitedUser } =
    useLimitedPlanInfo();
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const getInitial = () => {
    const info = account?.account?.user_info;
    if (info?.first_name) {
      return info.first_name[0].toUpperCase();
    }
    if (info?.last_name) {
      return info.last_name[0].toUpperCase();
    }
    if (info?.email) {
      return info.email[0].toUpperCase();
    }
    return 'U';
  };

  const closeMobileSidebar = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsMobileOpen(false);
      setIsAnimatingOut(false);
    }, 300);
  };

  const handleNewChatClick = () => {
    startNewConversation();
    navigate('/deepsearch/chat');
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleAccountClick = () => {
    onAccountClick?.();
    if (isMobile) {
      closeMobileSidebar();
    }
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
  };

  const handleToggleCollapsed = (value: boolean) => {
    setCollapsed(value && isDesktop);
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  const modals = (
    <>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
      <ProfileModal
        open={openProfileModal}
        onClose={() => {
          setOpenProfileModal(false);
          if (isMobile) {
            closeMobileSidebar();
          }
        }}
        onAccountClick={handleAccountClick}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
        onLogout={onLogout}
      />
    </>
  );

  if (isMobile) {
    return (
      <>
        <ChatMenuStack>
          <BrandLogoChat
            width="112px"
            height="18px"
            sx={{ transform: 'translateY(0) translateX(0) scale(0.7)' }}
          />
          <MenuIcon
            onClick={() => {
              if (isMobileOpen) {
                closeMobileSidebar();
              } else {
                setIsMobileOpen(true);
              }
            }}
          />
        </ChatMenuStack>

        {(isMobileOpen || isAnimatingOut) && (
          <ChatPageBoxContainer
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100dvh"
            boxShadow="4px 0 8px rgba(0, 0, 0, 0.15)"
            zIndex={1000}
            isAnimatingOut={isAnimatingOut}
          >
            <Stack
              spacing={3}
              padding={3}
              height="100%"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
              }}
            >
              <Stack
                alignItems="center"
                spacing={2}
                marginBottom={4}
                flexDirection="row"
                justifyContent="space-between"
                width="100%"
                sx={{ flexShrink: 0 }}
              >
                <BrandLogoChat width="116px" height="16px" />
                <CloseSidebarButton
                  type="button"
                  aria-label="Close sidebar"
                  onClick={closeMobileSidebar}
                >
                  <Close sx={{ marginTop: '0 !important' }} />
                </CloseSidebarButton>
              </Stack>

              <Stack spacing={2} sx={{ flexShrink: 0 }}>
                <MobileNavItem onClick={handleNewChatClick}>
                  <NewChatButton variant="contained">
                    <AddIcon
                      sx={{
                        width: '20px',
                        height: '20px',
                        color: theme.palette.text.contrastText,
                      }}
                    />
                  </NewChatButton>
                  <Text fontWeight="bold" color={theme.palette.primary.main}>
                    New Chat
                  </Text>
                </MobileNavItem>
                <MobileNavItem onClick={handleRecentReportsClick}>
                  <InsertDriveFileIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Text fontWeight="bold" color={theme.palette.primary.main}>
                    Report Library
                  </Text>
                </MobileNavItem>
              </Stack>

              <Stack
                spacing={0.5}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ marginTop: '8px', marginBottom: 2, flexShrink: 0 }}
                >
                  <QuestionAnswer
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Text fontWeight="bold" color={theme.palette.primary.main}>
                    Chat History
                  </Text>
                </Stack>
                <Stack
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      height: 16,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      bgcolor: 'background.default',
                    }}
                  />
                  <Box sx={{ flexShrink: 0, minHeight: 'min-content' }}>
                    <DeepSearchConversationList
                      collapsed={false}
                      onConversationClick={closeMobileSidebar}
                    />
                  </Box>
                </Stack>
              </Stack>

              <Stack
                spacing={2}
                sx={{
                  flexShrink: 0,
                  paddingBottom: 'env(safe-area-inset-bottom)',
                }}
              >
                <MobileNavItem onClick={handleAccountClick}>
                  <PersonIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      marginTop: '-1px !important',
                      color: theme.palette.text.secondary,
                    }}
                  />
                  <Text fontWeight="bold" color={theme.palette.primary.main}>
                    Account
                  </Text>
                </MobileNavItem>
                <MobileProfileBox ref={profileRef} onClick={handleProfileClick}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <SidebarAvatar>{getInitial()}</SidebarAvatar>
                    <ProfileSection account={account} />
                  </Stack>
                </MobileProfileBox>
              </Stack>
            </Stack>
          </ChatPageBoxContainer>
        )}

        {modals}
      </>
    );
  }

  return (
    <ChatPageSidebarContainer>
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <Stack spacing={4} paddingTop={6} alignItems="center">
            <DarkTooltip title={collapsed ? 'Expand' : ''}>
              <SidebarIcon>
                {collapsed ? (
                  <KeyboardTab onClick={() => handleToggleCollapsed(false)} />
                ) : (
                  <KeyboardBackspace
                    onClick={() => handleToggleCollapsed(true)}
                  />
                )}
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'New Chat' : ''}>
              <NewChatButton
                variant="contained"
                onClick={handleNewChatClick}
                sx={{
                  '&:hover': { backgroundColor: theme.palette.button.dark },
                }}
              >
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Report Library' : ''}>
              <SidebarIcon onClick={handleRecentReportsClick}>
                <InsertDriveFileIcon sx={{ width: '20px', height: '20px' }} />
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Chat History' : ''}>
              <SidebarIcon onClick={() => handleToggleCollapsed(!collapsed)}>
                <QuestionAnswer sx={{ width: '20px', height: '20px' }} />
              </SidebarIcon>
            </DarkTooltip>
          </Stack>
          <Stack
            sx={{ cursor: 'pointer' }}
            spacing={2}
            alignItems="center"
            paddingBottom={7.5}
            onClick={handleProfileClick}
          >
            <DarkTooltip title={collapsed ? 'Profile' : ''}>
              <span>
                <SidebarAvatar>{getInitial()}</SidebarAvatar>
              </span>
            </DarkTooltip>
          </Stack>
        </Stack>

        <Stack
          height="100%"
          sx={{
            py: 2,
            transition: 'width 0.3s ease-in-out',
            transitionDelay: collapsed ? '0.1s' : '0',
          }}
          width={collapsed ? '0px' : '150px'}
        >
          <Stack
            spacing={3}
            paddingTop={5}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0' : '0.2s',
            }}
          >
            <BrandLogoChat width="108px" height="40px" />
            <SidebarNavText $marginTop="25px" onClick={handleNewChatClick}>
              New Chat
            </SidebarNavText>
            <SidebarNavText
              $marginTop="39px"
              onClick={handleRecentReportsClick}
            >
              Report Library
            </SidebarNavText>
            <SidebarNavText
              $marginTop="35px"
              onClick={() => handleToggleCollapsed(!collapsed)}
            >
              Chat History
            </SidebarNavText>
          </Stack>

          <ScrollableConversationBox>
            <DeepSearchConversationList collapsed={collapsed} />
          </ScrollableConversationBox>

          <Stack
            spacing={3}
            paddingBottom={3.75}
            sx={{
              flexShrink: 0,
              marginTop: 'auto',
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0s' : '0.2s',
            }}
          >
            {isLimitedUser && (
              <ReportUsageCard
                reportsRun={reportsRun}
                reportsLimit={reportLimit}
                progressValue={reportsRunPercentage}
                sx={{
                  marginLeft: `-48px !important`,
                  marginRight: '8px !important',
                }}
                onUpgradeClick={handleUpgradePlanClick}
              />
            )}
            <AccountBox onClick={handleAccountClick}>
              <PersonIcon
                className="account-icon"
                sx={{
                  width: '20px',
                  height: '20px',
                  marginTop: '-2px !important',
                  color: theme.palette.text.secondary,
                }}
              />
              <AccountNavText className="account-text">Account</AccountNavText>
            </AccountBox>
            <Box
              ref={profileRef}
              onClick={handleProfileClick}
              sx={{ cursor: 'pointer' }}
            >
              <ProfileSection account={account} />
            </Box>
          </Stack>
        </Stack>
      </Box>

      {modals}
    </ChatPageSidebarContainer>
  );
};
